/**
 * Call Sound Manager
 * Handles all call-related sounds with fallback to generated tones
 */

class CallSoundManager {
  private audioContext: AudioContext | null = null;
  private currentSound: HTMLAudioElement | null = null;
  private oscillators: OscillatorNode[] = [];

  constructor() {
    // Initialize AudioContext on user interaction
    if (typeof window !== 'undefined') {
      document.addEventListener('click', () => {
        if (!this.audioContext) {
          this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        }
      }, { once: true });
    }
  }

  /**
   * Play incoming call ringtone (looping)
   */
  playIncomingRingtone(): HTMLAudioElement | null {
    this.stopAll();

    // Try to load audio file first
    const audio = new Audio('/sounds/ringtone.mp3');
    audio.loop = true;
    audio.volume = 0.6;

    audio.play().catch(() => {
      console.log('📞 Could not play ringtone file, using generated tone');
      this.playGeneratedRingtone();
    });

    this.currentSound = audio;
    return audio;
  }

  /**
   * Generate a pleasant ringtone using Web Audio API
   */
  private playGeneratedRingtone() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    }

    const playTone = () => {
      // Create a pleasant dual-tone ringtone (like iPhone)
      const frequencies = [800, 1000];
      const duration = 0.4;

      frequencies.forEach((freq, index) => {
        const oscillator = this.audioContext!.createOscillator();
        const gainNode = this.audioContext!.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(this.audioContext!.destination);

        oscillator.frequency.value = freq;
        oscillator.type = 'sine';

        // Envelope
        gainNode.gain.setValueAtTime(0, this.audioContext!.currentTime);
        gainNode.gain.linearRampToValueAtTime(0.3, this.audioContext!.currentTime + 0.1);
        gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext!.currentTime + duration);

        oscillator.start(this.audioContext!.currentTime + (index * 0.1));
        oscillator.stop(this.audioContext!.currentTime + duration + (index * 0.1));

        this.oscillators.push(oscillator);
      });

      // Repeat every 2 seconds
      setTimeout(() => {
        if (this.oscillators.length > 0) {
          playTone();
        }
      }, 2000);
    };

    playTone();
  }

  /**
   * Play outgoing call ringing tone
   */
  playOutgoingRingtone(): HTMLAudioElement | null {
    this.stopAll();

    const audio = new Audio('/sounds/calling.mp3');
    audio.loop = true;
    audio.volume = 0.5;

    audio.play().catch(() => {
      console.log('📞 Could not play calling sound, using generated tone');
      this.playGeneratedOutgoingTone();
    });

    this.currentSound = audio;
    return audio;
  }

  /**
   * Generate outgoing call tone
   */
  private playGeneratedOutgoingTone() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    }

    const playTone = () => {
      const oscillator = this.audioContext!.createOscillator();
      const gainNode = this.audioContext!.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext!.destination);

      oscillator.frequency.value = 440;
      oscillator.type = 'sine';

      gainNode.gain.setValueAtTime(0.2, this.audioContext!.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext!.currentTime + 1);

      oscillator.start();
      oscillator.stop(this.audioContext!.currentTime + 1);

      this.oscillators.push(oscillator);

      setTimeout(() => {
        if (this.oscillators.length > 0) {
          playTone();
        }
      }, 3000);
    };

    playTone();
  }

  /**
   * Play call connected sound
   */
  playConnectedSound() {
    this.stopAll();

    const audio = new Audio('/sounds/connected.mp3');
    audio.volume = 0.4;

    audio.play().catch(() => {
      console.log('📞 Could not play connected sound, using generated tone');
      this.playGeneratedConnectedTone();
    });
  }

  /**
   * Generate connected tone (short positive beep)
   */
  private playGeneratedConnectedTone() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    }

    const frequencies = [600, 800];
    frequencies.forEach((freq, index) => {
      const oscillator = this.audioContext!.createOscillator();
      const gainNode = this.audioContext!.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext!.destination);

      oscillator.frequency.value = freq;
      oscillator.type = 'sine';

      const startTime = this.audioContext!.currentTime + (index * 0.1);
      gainNode.gain.setValueAtTime(0.2, startTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + 0.2);

      oscillator.start(startTime);
      oscillator.stop(startTime + 0.2);
    });
  }

  /**
   * Play call ended sound
   */
  playEndedSound() {
    this.stopAll();

    const audio = new Audio('/sounds/call-end.mp3');
    audio.volume = 0.4;

    audio.play().catch(() => {
      console.log('📞 Could not play ended sound, using generated tone');
      this.playGeneratedEndedTone();
    });
  }

  /**
   * Generate ended tone (short descending beep)
   */
  private playGeneratedEndedTone() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    }

    const oscillator = this.audioContext!.createOscillator();
    const gainNode = this.audioContext!.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(this.audioContext!.destination);

    oscillator.frequency.setValueAtTime(600, this.audioContext!.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(300, this.audioContext!.currentTime + 0.3);
    oscillator.type = 'sine';

    gainNode.gain.setValueAtTime(0.2, this.audioContext!.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext!.currentTime + 0.3);

    oscillator.start();
    oscillator.stop(this.audioContext!.currentTime + 0.3);
  }

  /**
   * Play call busy/rejected sound
   */
  playBusySound() {
    this.stopAll();

    const audio = new Audio('/sounds/busy.mp3');
    audio.volume = 0.4;

    audio.play().catch(() => {
      console.log('📞 Could not play busy sound, using generated tone');
      this.playGeneratedBusyTone();
    });
  }

  /**
   * Generate busy tone (rapid beeps)
   */
  private playGeneratedBusyTone() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    }

    for (let i = 0; i < 3; i++) {
      const oscillator = this.audioContext!.createOscillator();
      const gainNode = this.audioContext!.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext!.destination);

      oscillator.frequency.value = 480;
      oscillator.type = 'square';

      const startTime = this.audioContext!.currentTime + (i * 0.5);
      gainNode.gain.setValueAtTime(0.15, startTime);
      gainNode.gain.setValueAtTime(0, startTime + 0.25);

      oscillator.start(startTime);
      oscillator.stop(startTime + 0.25);
    }
  }

  /**
   * Stop all currently playing sounds
   */
  stopAll() {
    // Stop HTML audio
    if (this.currentSound) {
      this.currentSound.pause();
      this.currentSound.currentTime = 0;
      this.currentSound = null;
    }

    // Stop all oscillators
    this.oscillators.forEach(osc => {
      try {
        osc.stop();
        osc.disconnect();
      } catch (e) {
        // Already stopped
      }
    });
    this.oscillators = [];
  }

  /**
   * Cleanup on unmount
   */
  destroy() {
    this.stopAll();
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
  }
}

// Export singleton instance
export const callSounds = new CallSoundManager();
